import { SignUpGuardProvider } from '@app/hooks/SignUpGuardContext';
import { useEngageTextMessaging } from '@app/hooks/useEngageTextMessaging';
import { asNonEmptyArray } from '@app/types/utils';
import { Box, constants, PageSection } from '@redbubble/design-system';
import { RedbubbleLayout } from '@redbubble/next/app';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { RBLayoutProps, UserInfo, WithLayout } from '@redbubble/next/types';
import { ExperimentConfig } from '@redbubble/next-amplitude';
import { useUserInfo } from '@redbubble/next-provider';
import { useAmplitudeExperiment } from '@redbubble/next-provider/amplitude';
import {
  RSPageType,
  useRSPageView,
} from '@redbubble/next-provider/rudderstack';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { TrustSignalsGroup } from '../i/components/TrustSignalsGroup';
import { DynamicSubscribeBannerFooter } from '../shop/components/SubscribeBanner/DynamicSubscribeBanner';
import { ArtistFeed } from './components/ArtistCard';
import { FanArtSection } from './components/FanArtSection/FanArtSection';
import { HeroBanner } from './components/HeroBanner';
import { ProductFeed } from './components/ProductFeed';
import { RecommendedWorksFeed } from './components/RecommendedWorksFeed';
import { SEOTags } from './components/SEOTags';
import { TileContainer } from './components/TileContainer/TileContainer';
import styles from './index.module.scss';
import type { HomePageProps } from './types';

const SignUpGuard = dynamic(
  () =>
    import(
      /* webpackChunkName: "SignUpGuard" */
      '@app/components/SignUpGuard/SignUpGuard'
    ).then(({ SignUpGuard: Component }) => Component),
  { ssr: false, loading: () => null }
);

const ReactToastContainer = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReactToastContainer" */
      '@app/components/Toasts/ReactToastContainer'
    ).then(({ ReactToastContainer: Component }) => Component),
  { ssr: false, loading: () => null }
);

const HomePage: WithLayout<NextPage<HomePageProps>> = ({
  recommendedWorks,
  contentfulData,
  featuredArtists,
  fanArtProducts,
}) => {
  useRSPageView(RSPageType.Home);
  const [{ isLoggedIn }] = useUserInfo<UserInfo>();

  const heroBanner = contentfulData?.heroBanner;
  const tileList = contentfulData?.tileList;

  const artists = asNonEmptyArray(featuredArtists);
  const recommendations = asNonEmptyArray(recommendedWorks);

  // biome-ignore lint/correctness/noUnusedVariables: Ignoring for DE to test
  const exp = useAmplitudeExperiment(ExperimentConfig.RBExperimentProduction);

  return (
    <Box element="main">
      <SignUpGuardProvider>
        <SignUpGuard />
        <SEOTags />
        {heroBanner && (
          <ErrorBoundary>
            <PageSection backgroundColor={constants.COLOR_WHITE}>
              <HeroBanner herobanner={heroBanner} />
            </PageSection>
          </ErrorBoundary>
        )}
        {tileList && (
          <ErrorBoundary>
            <div className={styles.sectionSpacing}>
              <PageSection
                profile={constants.DESKTOP}
                size={constants.SMALL}
                marginTop={0}
                backgroundColor={constants.COLOR_WHITE}
              >
                <TileContainer tiles={tileList} />
              </PageSection>
            </div>
          </ErrorBoundary>
        )}

        <ErrorBoundary>
          <ProductFeed />
        </ErrorBoundary>
        {recommendations && (
          <ErrorBoundary>
            <PageSection
              profile={constants.DESKTOP}
              size={constants.SMALL}
              marginTop={1}
              paddingTop={2}
              backgroundColor={constants.COLOR_WHITE}
            >
              <RecommendedWorksFeed recommendedWorks={recommendations} />
            </PageSection>
          </ErrorBoundary>
        )}
        {artists && (
          <ErrorBoundary>
            <PageSection
              profile={constants.DESKTOP}
              size={constants.SMALL}
              marginTop={1}
              paddingTop={2}
              backgroundColor={constants.COLOR_WHITE}
            >
              <ArtistFeed artists={artists} />
            </PageSection>
          </ErrorBoundary>
        )}
        {fanArtProducts && (
          <ErrorBoundary>
            <PageSection
              profile={constants.DESKTOP}
              size={constants.SMALL}
              marginTop={1}
              paddingTop={2}
              backgroundColor={constants.COLOR_WHITE}
              paddingBottom={2}
            >
              <FanArtSection items={fanArtProducts} />
            </PageSection>
          </ErrorBoundary>
        )}
        <ErrorBoundary>
          <div className={styles.sectionSpacing}>
            <PageSection
              profile={constants.DESKTOP}
              backgroundColor={constants.COLOR_WHITE}
              size={constants.SMALL}
              marginTop={0}
            >
              <TrustSignalsGroup />
            </PageSection>
          </div>
        </ErrorBoundary>
        {!isLoggedIn && (
          <ErrorBoundary>
            <DynamicSubscribeBannerFooter />
          </ErrorBoundary>
        )}
      </SignUpGuardProvider>
      <ReactToastContainer />
    </Box>
  );
};

const HomePageLayout = (props: RBLayoutProps) => {
  const engagingPlaceholderMessages = useEngageTextMessaging();

  return RedbubbleLayout(props, {
    engagingPlaceholderMessages,
  });
};

HomePage.layout = HomePageLayout;

export default HomePage;
